import { useState } from 'react';
import type { Period } from '@casetrace/shared';
import {
  useCreatePeriod,
  useUpdatePeriod,
  type CreatePeriodInput,
  type UpdatePeriodInput,
} from '../../hooks/use-period-mutations';
import { DEFAULT_PERIOD_COLOR } from './period-colors';
import {
  dateInputToPeriodFrom,
  dateInputToPeriodTo,
  isoTimestampToDateInput,
} from './period-date-input';

export function usePeriodEditorForm(caseId: string, period: Period | null, onClose: () => void) {
  const isEdit = period !== null;
  const createMutation = useCreatePeriod(caseId);
  const updateMutation = useUpdatePeriod(caseId);
  const pending = createMutation.isPending || updateMutation.isPending;

  const [label, setLabel] = useState(period?.label ?? '');
  const [description, setDescription] = useState(period?.description ?? '');
  const [fromDate, setFromDate] = useState(period ? isoTimestampToDateInput(period.from) : '');
  const [toDate, setToDate] = useState(period ? isoTimestampToDateInput(period.to) : '');
  const [color, setColor] = useState(period?.color ?? DEFAULT_PERIOD_COLOR);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [titleTouched, setTitleTouched] = useState(false);

  const { labelError, rangeError, canSubmit } = validatePeriodForm(
    label,
    fromDate,
    toDate,
    pending,
  );
  const displayError = resolveDisplayError(
    submitError,
    createMutation.error ?? updateMutation.error,
  );

  const submit = async () => {
    setTitleTouched(true);
    if (!canSubmit) return;
    setSubmitError(null);

    try {
      await persistPeriod({
        isEdit,
        period,
        label,
        description,
        fromDate,
        toDate,
        color,
        createMutation,
        updateMutation,
      });
      onClose();
    } catch (err) {
      setSubmitError(readErrorMessage(err));
    }
  };

  return {
    isEdit,
    label,
    setLabel,
    description,
    setDescription,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    color,
    setColor,
    titleTouched,
    setTitleTouched,
    labelError,
    rangeError,
    canSubmit,
    pending,
    displayError,
    submit,
  };
}

function validatePeriodForm(
  label: string,
  fromDate: string,
  toDate: string,
  pending: boolean,
): { labelError?: string; rangeError?: string; canSubmit: boolean } {
  const labelError = label.trim().length === 0 ? 'Tittel kan ikke være tom.' : undefined;
  const rangeError =
    fromDate && toDate && fromDate > toDate ? 'Til-dato kan ikke være før fra-dato.' : undefined;
  const canSubmit =
    !labelError && !rangeError && fromDate.length > 0 && toDate.length > 0 && !pending;
  return { labelError, rangeError, canSubmit };
}

function resolveDisplayError(submitError: string | null, mutationError: unknown): string | null {
  if (submitError) return submitError;
  if (mutationError instanceof Error) return mutationError.message;
  if (mutationError) return 'Kunne ikke lagre.';
  return null;
}

function readErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Kunne ikke lagre tidsslotten.';
}

interface PersistPeriodArgs {
  readonly isEdit: boolean;
  readonly period: Period | null;
  readonly label: string;
  readonly description: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly color: string;
  readonly createMutation: ReturnType<typeof useCreatePeriod>;
  readonly updateMutation: ReturnType<typeof useUpdatePeriod>;
}

async function persistPeriod(args: PersistPeriodArgs): Promise<void> {
  const from = dateInputToPeriodFrom(args.fromDate);
  const to = dateInputToPeriodTo(args.toDate);
  const description = args.description.trim() || undefined;

  if (args.isEdit && args.period) {
    const input: UpdatePeriodInput = {
      periodId: args.period.id,
      label: args.label,
      description,
      from,
      to,
      color: args.color,
    };
    await args.updateMutation.mutateAsync(input);
    return;
  }

  const input: CreatePeriodInput = {
    label: args.label,
    description,
    from,
    to,
    color: args.color,
  };
  await args.createMutation.mutateAsync(input);
}
